/**
 * @jinzhuan/api-client — single-source-of-truth typed client for the
 * Jinzhuan backend API.
 *
 * Frontend code MUST consume `createApiClient` (or the lower-level `paths`
 * type) instead of hand-writing fetch calls. This guarantees:
 *
 *   1. Compile-time alignment with the OpenAPI document at
 *      `docs/api/openapi.yaml`. Backend schema drift produces TypeScript
 *      errors in the frontend at build time.
 *   2. No "fake API" in the frontend: every endpoint reachable by the client
 *      is also defined in the backend Swagger metadata.
 *   3. A consistent error envelope: non-OK responses surface a structured
 *      `ApiError` with `status`, `code`, `message`, and the raw response.
 *
 * Auth pattern:
 *   - Caller supplies a `getAccessToken()` async hook.
 *   - The wrapper attaches `Authorization: Bearer <token>` to every request.
 *   - Refresh-token rotation is the caller's responsibility; this package
 *     deliberately stays unopinionated about storage (web localStorage vs
 *     mobile SecureStore).
 */
import createClient, { type Middleware } from 'openapi-fetch';
import type { paths, components, operations } from './schema';

export type ApiPaths = paths;
export type ApiComponents = components;
export type ApiOperations = operations;

/**
 * Structured error thrown by the typed client when an HTTP call returns a
 * non-2xx response or when the body cannot be parsed as JSON.
 */
export class ApiError extends Error {
  public readonly status: number;
  public readonly code?: string;
  public readonly response: Response;
  public readonly bodyText?: string;

  constructor(init: { status: number; message: string; code?: string; response: Response; bodyText?: string }) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.response = init.response;
    this.bodyText = init.bodyText;
  }
}

export interface CreateApiClientOptions {
  /** Absolute base URL, e.g. `https://api.jinzhuan.example.com`. */
  baseUrl: string;
  /**
   * Returns the current access token, or `null`/`undefined` if anonymous.
   * Awaited per request so callers can refresh-on-demand.
   */
  getAccessToken?: () => string | null | undefined | Promise<string | null | undefined>;
  /**
   * Optional org-id header for endpoints that cannot derive org from the
   * access token (specifically `/auth/refresh`). Most endpoints derive org
   * from the JWT and ignore this header.
   */
  getOrgId?: () => string | null | undefined | Promise<string | null | undefined>;
  /** Optional global headers (e.g. correlation IDs, request IDs). */
  defaultHeaders?: Record<string, string>;
  /** Inject a custom fetch implementation (e.g. RN, MSW). */
  fetch?: typeof fetch;
  /**
   * Control cookie credentials behavior.
   * Defaults to 'include' to support HttpOnly session cookies.
   */
  credentials?: RequestCredentials;
}

/**
 * Create a typed API client bound to the supplied baseUrl + auth provider.
 *
 * Example:
 *   const api = createApiClient({ baseUrl: '/api', getAccessToken: () => token });
 *   const { data, error } = await api.POST('/api/v1/auth/login', {
 *     body: { tenantSlug: 'demo', email, password },
 *   });
 */
/**
 * Helper to extract CSRF token from document.cookie in browser environments.
 */
function getCsrfFromCookie(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(/jzc_csrf=([^;]+)/);
  return match ? match[1] : undefined;
}

export function createApiClient(opts: CreateApiClientOptions): ReturnType<typeof createClient<paths>> {
  const client = createClient<paths>({
    baseUrl: opts.baseUrl,
    fetch: opts.fetch,
    credentials: opts.credentials ?? 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(opts.defaultHeaders ?? {}),
    },
  });

  const authMiddleware: Middleware = {
    async onRequest({ request }) {
      const token = await opts.getAccessToken?.();
      if (token) {
        request.headers.set('Authorization', `Bearer ${token}`);
      }
      const orgId = await opts.getOrgId?.();
      if (orgId) {
        request.headers.set('X-Org-Id', orgId);
      }

      // CSRF protection for mutations (POST, PUT, PATCH, DELETE)
      if (!['GET', 'HEAD', 'OPTIONS'].includes(request.method)) {
        const csrfToken = getCsrfFromCookie();
        if (csrfToken) {
          request.headers.set('X-CSRF-Token', csrfToken);
        }
      }

      return request;
    },
    async onResponse({ response }) {
      if (response.ok || response.status < 400) {
        return response;
      }
      const contentType = response.headers.get('content-type') ?? '';
      let bodyText: string | undefined;
      let code: string | undefined;
      let message = `HTTP ${response.status}`;
      try {
        const cloned = response.clone();
        bodyText = await cloned.text();
        if (contentType.includes('application/json') && bodyText.length > 0) {
          const parsed = JSON.parse(bodyText) as { message?: string; error?: string; code?: string };
          message = parsed.message ?? parsed.error ?? message;
          code = parsed.code;
        }
      } catch {
        // Leave bodyText undefined; throw with the bare HTTP status.
      }
      throw new ApiError({ status: response.status, message, code, response, bodyText });
    },
  };
  client.use(authMiddleware);
  return client;
}

// Re-export commonly used response/request shapes so frontend code can refer
// to them without spelling out the full `paths['/api/v1/...']` index. These
// are computed lazily so the schema file remains the single source of truth.
export type LoginRequest =
  paths['/api/v1/auth/login']['post']['requestBody']['content']['application/json'];
export type LoginResponse =
  paths['/api/v1/auth/login']['post']['responses']['200']['content']['application/json'];
export type MeResponse =
  paths['/api/v1/auth/me']['get']['responses']['200']['content']['application/json'];
export type RefreshRequest =
  paths['/api/v1/auth/refresh']['post']['requestBody']['content']['application/json'];
export type RefreshResponse =
  paths['/api/v1/auth/refresh']['post']['responses']['200']['content']['application/json'];
export type LogoutRequest =
  paths['/api/v1/auth/logout']['post']['requestBody']['content']['application/json'];

// ============================================================================
// Proposal AI Draft Review — type aliases
// ============================================================================

export type ProposalDto =
  paths['/api/v1/proposals']['get']['responses']['200']['content']['application/json'][number];

export type CreateProposalRequest =
  paths['/api/v1/proposals']['post']['requestBody']['content']['application/json'];

export type UpdateProposalRequest =
  paths['/api/v1/proposals/{id}']['put']['requestBody']['content']['application/json'];

export type ProposalVersionDto =
  paths['/api/v1/proposals/{id}/versions']['get']['responses']['200']['content']['application/json'][number];

export type CreateProposalVersionRequest =
  paths['/api/v1/proposals/{id}/versions']['post']['requestBody']['content']['application/json'];

export type GenerateAiDraftRequest =
  paths['/api/v1/proposals/{id}/ai-draft']['post']['requestBody']['content']['application/json'];

export type AiDraftResultDto =
  paths['/api/v1/proposals/{id}/ai-draft']['post']['responses']['201']['content']['application/json'];

export type ProposalReviewDto =
  paths['/api/v1/proposals/{id}/reviews']['get']['responses']['200']['content']['application/json'][number];

export type CreateProposalReviewRequest =
  paths['/api/v1/proposals/{id}/reviews']['post']['requestBody']['content']['application/json'];

export type TransitionProposalRequest =
  paths['/api/v1/proposals/{id}/submit-review']['post']['requestBody']['content']['application/json'];

// ============================================================================
// Error Reports — type aliases (Issue #178)
// ============================================================================

export type CreateErrorReportRequest =
  paths['/api/v1/error-reports']['post']['requestBody']['content']['application/json'];

export type ErrorReportDto =
  paths['/api/v1/error-reports']['post']['responses']['201']['content']['application/json'];

// ============================================================================
// AI Model Configuration Center — type aliases (Issue #187)
// ============================================================================

export type LlmProviderDto =
  paths['/api/v1/ai-model-center/providers']['get']['responses']['200']['content']['application/json'][number];

export type CreateLlmProviderRequest =
  paths['/api/v1/ai-model-center/providers']['post']['requestBody']['content']['application/json'];

export type UpdateLlmProviderRequest =
  paths['/api/v1/ai-model-center/providers/{id}']['patch']['requestBody']['content']['application/json'];

export type LlmModelDto =
  paths['/api/v1/ai-model-center/models']['get']['responses']['200']['content']['application/json'][number];

export type UpsertLlmModelRequest =
  paths['/api/v1/ai-model-center/models']['post']['requestBody']['content']['application/json'];

export type UpdateLlmModelRequest =
  paths['/api/v1/ai-model-center/models/{id}']['patch']['requestBody']['content']['application/json'];

export type LlmCredentialDto =
  paths['/api/v1/ai-model-center/providers/{id}/credentials']['get']['responses']['200']['content']['application/json'][number];

export type CreateLlmCredentialRequest =
  paths['/api/v1/ai-model-center/providers/{id}/credentials']['post']['requestBody']['content']['application/json'];

export type LlmCallAuditDto =
  paths['/api/v1/ai-model-center/call-audits']['get']['responses']['200']['content']['application/json'][number];

export type RecordLlmCallAuditRequest =
  paths['/api/v1/ai-model-center/call-audits']['post']['requestBody']['content']['application/json'];

export type ReviewLlmCallAuditRequest =
  paths['/api/v1/ai-model-center/call-audits/{id}/review']['patch']['requestBody']['content']['application/json'];

export type LlmCallAuditAggregateDto =
  paths['/api/v1/ai-model-center/call-audits/usage-summary']['get']['responses']['200']['content']['application/json'];

export type LlmQuotaDto =
  paths['/api/v1/ai-model-center/quotas']['get']['responses']['200']['content']['application/json'][number];

export type UpsertLlmQuotaRequest =
  paths['/api/v1/ai-model-center/quotas']['post']['requestBody']['content']['application/json'];

export type LlmProviderSyncRunDto =
  paths['/api/v1/ai-model-center/providers/{id}/sync-runs']['get']['responses']['200']['content']['application/json'][number];

export type TriggerProviderSyncResponse =
  paths['/api/v1/ai-model-center/providers/{id}/sync']['post']['responses']['201']['content']['application/json'];

// ============================================================================
// Dashboard read-model + export tasks — type aliases (Issue #189)
// ============================================================================

export type DashboardOverviewResponse =
  paths['/api/v1/dashboard/overview']['get']['responses']['200']['content']['application/json'];

export type DashboardMetric =
  DashboardOverviewResponse['metrics'][number];

export type RevenueSummaryResponse =
  paths['/api/v1/dashboard/revenue-summary']['get']['responses']['200']['content']['application/json'];

export type ExportTaskDto =
  paths['/api/v1/dashboard/exports']['get']['responses']['200']['content']['application/json'][number];

export type CreateExportTaskRequest =
  paths['/api/v1/dashboard/exports']['post']['requestBody']['content']['application/json'];

export type CancelExportTaskRequest =
  paths['/api/v1/dashboard/exports/{id}/cancel']['post']['requestBody']['content']['application/json'];

export type RequestExportDownloadUrlRequest =
  paths['/api/v1/dashboard/exports/{id}/download-url']['post']['requestBody']['content']['application/json'];

export type ExportDownloadUrlResponse =
  paths['/api/v1/dashboard/exports/{id}/download-url']['post']['responses']['200']['content']['application/json'];

// ============================================================================
// SMS admin — type aliases (Issue #196)
// ============================================================================

export type SmsTemplateDto =
  paths['/api/v1/admin/sms/templates']['get']['responses']['200']['content']['application/json'][number];

export type CreateSmsTemplateRequest =
  paths['/api/v1/admin/sms/templates']['post']['requestBody']['content']['application/json'];

export type UpdateSmsTemplateRequest =
  paths['/api/v1/admin/sms/templates/{id}']['patch']['requestBody']['content']['application/json'];

export type SmsSendLogDto =
  paths['/api/v1/admin/sms/logs']['get']['responses']['200']['content']['application/json'][number];

export type SmsDefaultSeedResult =
  paths['/api/v1/admin/sms/default-templates/seed']['post']['responses']['201']['content']['application/json'];

// ============================================================================
// Audit Log search & export — type aliases (Issue #248 / #287)
// ============================================================================

export type AuditLogListResponse =
  paths['/api/v1/audit-logs']['get']['responses']['200']['content']['application/json'];

export type AuditLogEntryDto = AuditLogListResponse['rows'][number];

export type AuditLogListQuery = NonNullable<
  paths['/api/v1/audit-logs']['get']['parameters']['query']
>;

export type AuditLogSummaryResponse =
  paths['/api/v1/audit-logs/summary']['get']['responses']['200']['content']['application/json'];

export type AuditLogSummaryBucketDto = AuditLogSummaryResponse['topActions'][number];

export type AuditLogResourceBucketDto = AuditLogSummaryResponse['topResources'][number];

export type AuditLogSummaryQuery = NonNullable<
  paths['/api/v1/audit-logs/summary']['get']['parameters']['query']
>;

export type AuditLogExportListResponse =
  paths['/api/v1/audit-logs/exports']['get']['responses']['200']['content']['application/json'];

export type AuditLogExportDto = AuditLogExportListResponse['rows'][number];

export type AuditLogExportListQuery = NonNullable<
  paths['/api/v1/audit-logs/exports']['get']['parameters']['query']
>;

// ============================================================================
// System Parameter Center — type aliases (Issue #267)
// ============================================================================

export type SystemParameterDto =
  paths['/api/v1/settings/system-parameters/catalog']['get']['responses']['200']['content']['application/json'][number];

export type SystemParameterValueDto =
  paths['/api/v1/settings/system-parameters/values']['get']['responses']['200']['content']['application/json'][number];

export type SystemParameterChangeListResponse =
  paths['/api/v1/settings/system-parameters/changes']['get']['responses']['200']['content']['application/json'];

export type SystemParameterChangeDto =
  SystemParameterChangeListResponse['rows'][number];

export type CreateSystemParameterChangeRequest =
  paths['/api/v1/settings/system-parameters/changes']['post']['requestBody']['content']['application/json'];

export type ApproveSystemParameterChangeRequest =
  paths['/api/v1/settings/system-parameters/changes/{id}/approve']['post']['requestBody']['content']['application/json'];

export type RejectSystemParameterChangeRequest =
  paths['/api/v1/settings/system-parameters/changes/{id}/reject']['post']['requestBody']['content']['application/json'];

export type RollbackSystemParameterChangeRequest =
  paths['/api/v1/settings/system-parameters/versions/{versionId}/rollback']['post']['requestBody']['content']['application/json'];

export type SystemParameterVersionDto =
  paths['/api/v1/settings/system-parameters/{parameterId}/versions']['get']['responses']['200']['content']['application/json'][number];

// ============================================================================
// Tenant & Organization governance — type aliases (Issue #268)
// ============================================================================

export type TenantOrganizationCurrentDto =
  paths['/api/v1/settings/tenant-organization/current']['get']['responses']['200']['content']['application/json'];

export type TenantOrganizationTenantDto = TenantOrganizationCurrentDto['tenant'];

export type TenantOrganizationPrimaryOrgDto =
  TenantOrganizationCurrentDto['primaryOrganization'];

export type OrganizationUnitDto =
  paths['/api/v1/settings/tenant-organization/units']['get']['responses']['200']['content']['application/json'][number];

export type GovernanceBoundariesDto =
  paths['/api/v1/settings/tenant-organization/governance-boundaries']['get']['responses']['200']['content']['application/json'];

export type TenantOrganizationAuditEventDto =
  paths['/api/v1/settings/tenant-organization/audit-events']['get']['responses']['200']['content']['application/json'][number];

// ============================================================================
// Admin Customer WeChat governance — type aliases (Issue #269)
// ============================================================================

export type AdminWechatBindingListResponse =
  paths['/api/v1/admin/customer-wechat/bindings']['get']['responses']['200']['content']['application/json'];

export type AdminWechatBindingSummaryDto =
  AdminWechatBindingListResponse['rows'][number];

export type AdminWechatBindingWorkOrderListResponse =
  paths['/api/v1/admin/customer-wechat/work-orders']['get']['responses']['200']['content']['application/json'];

export type AdminWechatBindingWorkOrderDto =
  AdminWechatBindingWorkOrderListResponse['rows'][number];

export type CreateAdminWechatBindingWorkOrderRequest =
  paths['/api/v1/admin/customer-wechat/work-orders']['post']['requestBody']['content']['application/json'];

export type SubmitAdminWechatBindingWorkOrderRequest =
  paths['/api/v1/admin/customer-wechat/work-orders/{id}/submit']['post']['requestBody']['content']['application/json'];

export type ApproveAdminWechatBindingWorkOrderRequest =
  paths['/api/v1/admin/customer-wechat/work-orders/{id}/approve']['post']['requestBody']['content']['application/json'];

export type RejectAdminWechatBindingWorkOrderRequest =
  paths['/api/v1/admin/customer-wechat/work-orders/{id}/reject']['post']['requestBody']['content']['application/json'];

export type CancelAdminWechatBindingWorkOrderRequest =
  paths['/api/v1/admin/customer-wechat/work-orders/{id}/cancel']['post']['requestBody']['content']['application/json'];

export type AdminWechatLoginPolicyDto =
  paths['/api/v1/admin/customer-wechat/login-policy']['get']['responses']['200']['content']['application/json'];

export type UpdateAdminWechatLoginPolicyRequest =
  paths['/api/v1/admin/customer-wechat/login-policy']['patch']['requestBody']['content']['application/json'];

// ============================================================================
// Enhanced Verification Policy Center -- type aliases (Issue #270)
// ============================================================================

export type EnhancedVerificationPolicyListResponse =
  paths['/api/v1/admin/enhanced-verification/policies']['get']['responses']['200']['content']['application/json'];

export type EnhancedVerificationPolicyDto =
  EnhancedVerificationPolicyListResponse['policies'][number];

export type EnhancedVerificationComplianceBlockerDto =
  EnhancedVerificationPolicyListResponse['complianceBlockers'][number];

export type UpdateEnhancedVerificationPolicyRequest =
  paths['/api/v1/admin/enhanced-verification/policies/{sceneKey}']['patch']['requestBody']['content']['application/json'];

export type EnhancedVerificationProviderListResponse =
  paths['/api/v1/admin/enhanced-verification/provider-readiness']['get']['responses']['200']['content']['application/json'];

export type EnhancedVerificationProviderReadinessDto =
  EnhancedVerificationProviderListResponse['providers'][number];

export type UpdateEnhancedVerificationProviderRequest =
  paths['/api/v1/admin/enhanced-verification/provider-readiness/{providerKey}']['patch']['requestBody']['content']['application/json'];

export type EnhancedVerificationFallbackListResponse =
  paths['/api/v1/admin/enhanced-verification/fallback-work-orders']['get']['responses']['200']['content']['application/json'];

export type EnhancedVerificationFallbackWorkOrderDto =
  EnhancedVerificationFallbackListResponse['rows'][number];

export type EnhancedVerificationAuditListResponse =
  paths['/api/v1/admin/enhanced-verification/audit-events']['get']['responses']['200']['content']['application/json'];

export type EnhancedVerificationAuditEventDto =
  EnhancedVerificationAuditListResponse['rows'][number];

// ============================================================================
// Session Idle-Timeout Policy — type aliases (Issue #289)
// ============================================================================

export type SessionPolicyResponse =
  paths['/api/v1/admin/session-policy']['get']['responses']['200']['content']['application/json'];

export type UpdateSessionPolicyRequest =
  paths['/api/v1/admin/session-policy']['patch']['requestBody']['content']['application/json'];

// ============================================================================
// Intake Assessment — type aliases (Issue #325)
// ============================================================================

export type IntakeAssessmentRecordListResponse =
  paths['/api/v1/intake-assessment/records']['get']['responses']['200']['content']['application/json'];

export type IntakeAssessmentRecordSummaryDto =
  IntakeAssessmentRecordListResponse['rows'][number];

export type IntakeAssessmentRecordDto =
  paths['/api/v1/intake-assessment/records/{id}']['get']['responses']['200']['content']['application/json'];

export type CreateIntakeAssessmentRecordRequest =
  paths['/api/v1/intake-assessment/records']['post']['requestBody']['content']['application/json'];

export type UpdateIntakeAssessmentRecordRequest =
  paths['/api/v1/intake-assessment/records/{id}']['put']['requestBody']['content']['application/json'];

export type SubmitIntakeAssessmentRecordRequest =
  paths['/api/v1/intake-assessment/records/{id}/submit']['post']['requestBody']['content']['application/json'];

export type ApproveIntakeAssessmentRecordRequest =
  paths['/api/v1/intake-assessment/records/{id}/approve']['post']['requestBody']['content']['application/json'];

export type RequestChangesIntakeAssessmentRecordRequest =
  paths['/api/v1/intake-assessment/records/{id}/request-changes']['post']['requestBody']['content']['application/json'];

export type CancelIntakeAssessmentRecordRequest =
  paths['/api/v1/intake-assessment/records/{id}/cancel']['post']['requestBody']['content']['application/json'];

export type IntakeAssessmentReviewDto =
  paths['/api/v1/intake-assessment/records/{id}/reviews']['get']['responses']['200']['content']['application/json'][number];

export type CustomerIntakeAssessmentSummaryDto =
  paths['/api/v1/intake-assessment/customers/{customerId}/approved-summary']['get']['responses']['200']['content']['application/json'];

// ============================================================================
// System Version — type aliases (Issue #339)
// ============================================================================

export type SystemVersionDto =
  paths['/api/v1/system/version']['get']['responses']['200']['content']['application/json'];

// ============================================================================
// Association Lookup — type aliases (Issue #344)
// ============================================================================

export type AssociationLookupQuery = NonNullable<
  paths['/api/v1/association-lookup/customers']['get']['parameters']['query']
>;

export type AssociationLookupResponse =
  paths['/api/v1/association-lookup/customers']['get']['responses']['200']['content']['application/json'];

export type AssociationLookupItem = AssociationLookupResponse['items'][number];

export type AssociationLookupKind = AssociationLookupItem['kind'];
